import React from "react";
import { ChevronDown, Check } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Grouped list of all supported leagues. Mirrors backend LEAGUE_MAP keys.
const GROUPS = [
  {
    label: "international",
    leagues: [
      { id: "world-cup", name: "FIFA World Cup 2026", flag: "🌍" },
      { id: "womens-world-cup", name: "Women's World Cup", flag: "🌍" },
      { id: "int-friendlies", name: "International Friendlies", flag: "🌍" },
      { id: "uefa-nations", name: "UEFA Nations League", flag: "🇪🇺" },
      { id: "wc-qualifiers-uefa", name: "WC Qualifiers UEFA", flag: "🇪🇺" },
      { id: "gold-cup", name: "CONCACAF Gold Cup", flag: "🌎" },
      { id: "copa-america", name: "Copa América", flag: "🌎" },
    ],
  },
  {
    label: "uefa_cups",
    leagues: [
      { id: "champions-league", name: "Champions League", flag: "🇪🇺" },
      { id: "europa-league", name: "Europa League", flag: "🇪🇺" },
      { id: "conference-league", name: "Conference League", flag: "🇪🇺" },
      { id: "libertadores", name: "Copa Libertadores", flag: "🌎" },
      { id: "afc-champions", name: "AFC Champions League", flag: "🌏" },
      { id: "caf-champions", name: "CAF Champions League", flag: "🌍" },
      { id: "concacaf-champions", name: "CONCACAF Champions Cup", flag: "🌎" },
    ],
  },
  {
    label: "top5",
    leagues: [
      { id: "premier-league", name: "Premier League", flag: "🏴󠁧󠁢󠁥󠁮󠁧󠁿" },
      { id: "la-liga", name: "La Liga", flag: "🇪🇸" },
      { id: "bundesliga", name: "Bundesliga", flag: "🇩🇪" },
      { id: "serie-a", name: "Serie A", flag: "🇮🇹" },
      { id: "ligue-1", name: "Ligue 1", flag: "🇫🇷" },
    ],
  },
  {
    label: "europe_other",
    leagues: [
      { id: "eredivisie", name: "Eredivisie", flag: "🇳🇱" },
      { id: "primeira-liga", name: "Primeira Liga", flag: "🇵🇹" },
      { id: "belgian-pro", name: "Belgian Pro League", flag: "🇧🇪" },
      { id: "scottish-premier", name: "Scottish Premiership", flag: "🏴󠁧󠁢󠁳󠁣󠁴󠁿" },
      { id: "super-lig", name: "Süper Lig", flag: "🇹🇷" },
      { id: "swiss-super", name: "Swiss Super League", flag: "🇨🇭" },
      { id: "austrian-bundesliga", name: "Austrian Bundesliga", flag: "🇦🇹" },
      { id: "greek-super", name: "Greek Super League", flag: "🇬🇷" },
      { id: "russian-premier", name: "Russian Premier League", flag: "🇷🇺" },
      { id: "czech-first", name: "Czech First League", flag: "🇨🇿" },
      { id: "israeli-premier", name: "Israeli Premier League", flag: "🇮🇱" },
      { id: "eliteserien", name: "Eliteserien", flag: "🇳🇴" },
      { id: "allsvenskan", name: "Allsvenskan", flag: "🇸🇪" },
      { id: "superliga-dk", name: "Danish Superliga", flag: "🇩🇰" },
    ],
  },
  {
    label: "second_div",
    leagues: [
      { id: "championship", name: "EFL Championship", flag: "🏴󠁧󠁢󠁥󠁮󠁧󠁿" },
      { id: "la-liga-2", name: "La Liga 2", flag: "🇪🇸" },
      { id: "bundesliga-2", name: "2. Bundesliga", flag: "🇩🇪" },
      { id: "serie-b", name: "Serie B", flag: "🇮🇹" },
      { id: "ligue-2", name: "Ligue 2", flag: "🇫🇷" },
      { id: "eerste-divisie", name: "Eerste Divisie", flag: "🇳🇱" },
    ],
  },
  {
    label: "cups",
    leagues: [
      { id: "fa-cup", name: "FA Cup", flag: "🏴󠁧󠁢󠁥󠁮󠁧󠁿" },
      { id: "copa-del-rey", name: "Copa del Rey", flag: "🇪🇸" },
      { id: "coppa-italia", name: "Coppa Italia", flag: "🇮🇹" },
      { id: "knvb-beker", name: "KNVB Beker", flag: "🇳🇱" },
    ],
  },
  {
    label: "americas",
    leagues: [
      { id: "mls", name: "MLS", flag: "🇺🇸" },
      { id: "nwsl", name: "NWSL", flag: "🇺🇸" },
      { id: "liga-mx", name: "Liga MX", flag: "🇲🇽" },
      { id: "brasileirao", name: "Brasileirão", flag: "🇧🇷" },
      { id: "primera-arg", name: "Liga Profesional (ARG)", flag: "🇦🇷" },
      { id: "colombia", name: "Liga BetPlay", flag: "🇨🇴" },
      { id: "chile", name: "Chilean Primera", flag: "🇨🇱" },
      { id: "uruguay", name: "Uruguayan Primera", flag: "🇺🇾" },
      { id: "peru", name: "Liga 1 Peru", flag: "🇵🇪" },
      { id: "ecuador", name: "LigaPro Ecuador", flag: "🇪🇨" },
      { id: "paraguay", name: "Paraguayan Primera", flag: "🇵🇾" },
      { id: "bolivia", name: "Bolivian Primera", flag: "🇧🇴" },
      { id: "venezuela", name: "Venezuelan Primera", flag: "🇻🇪" },
    ],
  },
  {
    label: "asia",
    leagues: [
      { id: "saudi-pro", name: "Saudi Pro League", flag: "🇸🇦" },
      { id: "j-league", name: "J1 League", flag: "🇯🇵" },
      { id: "a-league", name: "A-League", flag: "🇦🇺" },
      { id: "chinese-super", name: "Chinese Super League", flag: "🇨🇳" },
      { id: "indian-super", name: "Indian Super League", flag: "🇮🇳" },
    ],
  },
];

const ALL_LEAGUES_FLAT = GROUPS.flatMap((g) => g.leagues);

export default function LeagueSwitcher({ value, onChange, excludeAll = false, testIdPrefix = "league" }) {
  const { t } = useI18n();
  const current =
    value === "all"
      ? { id: "all", name: t("filter.all"), flag: "⚽" }
      : ALL_LEAGUES_FLAT.find((l) => l.id === value) || { id: value, name: value, flag: "⚽" };

  return (
    <div data-testid={`${testIdPrefix}-switcher`} className="flex items-center gap-2 flex-wrap">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={`${testIdPrefix}-trigger`}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.18em] border border-volt bg-volt text-black font-bold transition-colors"
          >
            <span className="text-base leading-none">{current.flag}</span>
            <span className="truncate max-w-[200px]">{current.name}</span>
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="bg-[#121212] border border-white/10 text-white rounded-none w-[280px] max-h-[70vh] overflow-y-auto"
        >
          {!excludeAll && (
            <>
              <DropdownMenuItem
                data-testid={`${testIdPrefix}-all`}
                onClick={() => onChange("all")}
                className={`flex items-center justify-between gap-3 cursor-pointer focus:bg-white/5 focus:text-volt ${
                  value === "all" ? "text-volt" : "text-white"
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="text-base">⚽</span>
                  <span className="text-sm">{t("filter.all")}</span>
                </span>
                {value === "all" && <Check className="w-4 h-4" />}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
            </>
          )}
          {GROUPS.map((group, gi) => (
            <React.Fragment key={group.label}>
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-normal py-2">
                {t(`leagues.group_${group.label}`)}
              </DropdownMenuLabel>
              {group.leagues.map((l) => (
                <DropdownMenuItem
                  key={l.id}
                  data-testid={`${testIdPrefix}-${l.id}`}
                  onClick={() => onChange(l.id)}
                  className={`flex items-center justify-between gap-3 cursor-pointer focus:bg-white/5 focus:text-volt ${
                    value === l.id ? "text-volt" : "text-white"
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-base leading-none shrink-0">{l.flag}</span>
                    <span className="text-sm truncate">{l.name}</span>
                  </span>
                  {value === l.id && <Check className="w-4 h-4 shrink-0" />}
                </DropdownMenuItem>
              ))}
              {gi < GROUPS.length - 1 && <DropdownMenuSeparator className="bg-white/10" />}
            </React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
